import { useState, useEffect, useCallback } from 'react';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { IssueCard, IssueCardSkeleton, IssueCardData } from '../ui/IssueCard';
import { IssueFilters, IssueFiltersState } from './IssueFilters';
import { LIST_ISSUES } from '../../graphql/queries';
import { ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

const client = generateClient();

type IssueCategory = 'ACCESSIBILITY' | 'EAA' | 'SEO' | 'BROKEN_LINK' | 'TOXIC_LINK';
type IssueSeverity = 'critical' | 'serious' | 'moderate' | 'minor';
type IssueSortField = 'SEVERITY' | 'PAGE_URL' | 'CATEGORY' | 'TYPE';
type SortOrder = 'ASC' | 'DESC';

interface IssueElement {
  html: string;
  selector: string;
  target?: string[];
  pageUrl?: string;
  failureSummary?: string;
}

interface UnifiedIssue {
  id: string;
  category: IssueCategory;
  severity: IssueSeverity;
  type: string;
  ruleId?: string;
  message: string;
  description: string;
  pageUrl: string;
  element?: IssueElement;
  affectedPages?: string[];
  affectedElements?: IssueElement[];
  remediation?: string;
  referenceUrl?: string;
  wcagCriteria?: string[];
  wcagLevel?: string;
  isWcag22?: boolean;
  toolSource?: 'AXE' | 'LIGHTHOUSE' | 'PA11Y';
  clauseNumber?: string;
  legalRisk?: string;
  statusCode?: number;
  isExternal?: boolean;
  needsManualReview?: boolean;
}

interface ListIssuesResponse {
  listIssues: {
    items: UnifiedIssue[];
    totalCount: number;
    nextToken?: string;
  };
}

interface AllIssuesTabProps {
  scanId: string;
  initialSeverityFilter?: IssueSeverity;
  /** Active risk acceptances, used to exclude reviewed/accepted issues from the list */
  acceptedRisks?: { issueId: string }[];
  /** Target URLs checked by hand in this scan, and the tick that records one */
  reviewedLinks?: string[];
  onToggleLinkReviewed?: (targetUrls: string[], reviewed: boolean) => void;
  /** Opens the review/accept dialog for a manual-review issue */
  onMarkReviewed?: (r: {
    issueId: string;
    issueDescription: string;
    severity?: string;
    category?: string;
    pageUrls?: string[];
  }) => void;
}

const PAGE_SIZE = 20;

export function AllIssuesTab({
  scanId,
  initialSeverityFilter,
  acceptedRisks = [],
  reviewedLinks = [],
  onToggleLinkReviewed,
  onMarkReviewed,
}: AllIssuesTabProps) {
  const [issues, setIssues] = useState<UnifiedIssue[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [pageTokens, setPageTokens] = useState<Record<number, string | undefined>>({
    1: undefined,
  });

  const [filters, setFilters] = useState<IssueFiltersState>({
    severity: initialSeverityFilter ? [initialSeverityFilter] : [],
    category: [],
    pageUrl: '',
    search: '',
    wcagLevel: [],
    manualReview: false,
  });

  const [sort, setSort] = useState<{ field: IssueSortField; order: SortOrder }>({
    field: 'SEVERITY',
    order: 'ASC',
  });

  const fetchIssues = useCallback(
    async (pageToken?: string) => {
      setIsLoading(true);
      setError(null);

      try {
        // Build filter input
        const filterInput: Record<string, unknown> = {};
        if (filters.severity.length > 0) filterInput.severity = filters.severity;
        if (filters.category.length > 0) filterInput.category = filters.category;
        if (filters.pageUrl) filterInput.pageUrl = filters.pageUrl;
        if (filters.search) filterInput.search = filters.search;
        if (filters.wcagLevel.length > 0) filterInput.wcagLevel = filters.wcagLevel;
        if (filters.manualReview) filterInput.needsManualReview = true;

        const response = (await client.graphql({
          query: LIST_ISSUES,
          variables: {
            scanId,
            limit: PAGE_SIZE,
            nextToken: pageToken,
            filters: Object.keys(filterInput).length > 0 ? filterInput : undefined,
            sort: { field: sort.field, order: sort.order },
            grouped: true,
          },
        })) as GraphQLResult<ListIssuesResponse>;

        if (response.data?.listIssues) {
          setIssues(response.data.listIssues.items);
          setTotalCount(response.data.listIssues.totalCount);
          setNextToken(response.data.listIssues.nextToken);
        }
      } catch (err) {
        console.error('Error fetching issues:', err);
        setError('Failed to load issues. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [scanId, filters, sort]
  );

  // Fetch issues when filters or sort changes
  useEffect(() => {
    setCurrentPage(1);
    setPageTokens({ 1: undefined });
    fetchIssues(undefined);
  }, [fetchIssues]);

  // Handle page change
  const goToPage = (page: number) => {
    if (page === currentPage) return;

    if (page > currentPage && nextToken) {
      // Going forward
      setPageTokens((prev) => ({ ...prev, [page]: nextToken }));
      fetchIssues(nextToken);
    } else if (page < currentPage && page in pageTokens) {
      // Going backward - use the stored token for that page. Page 1's token is
      // legitimately `undefined` (no token = fetch from the start), so check for the
      // KEY's presence, not a non-undefined value — otherwise page 1 was skipped and
      // the refetch never fired, leaving page 2's data showing under "page 1".
      fetchIssues(pageTokens[page]);
    }
    setCurrentPage(page);
  };

  // Convert grouped issue to IssueCardData
  const convertToIssueCardData = (issue: UnifiedIssue): IssueCardData => ({
    ruleId: issue.ruleId || issue.type,
    title: issue.message,
    description: issue.description,
    severity: issue.severity.toLowerCase() as 'critical' | 'serious' | 'moderate' | 'minor',
    wcagCriteria: issue.wcagCriteria,
    wcagLevel: issue.wcagLevel,
    toolSource: issue.toolSource,
    affectedElements:
      issue.affectedElements && issue.affectedElements.length > 0
        ? issue.affectedElements.map((el) => ({
            selector: el.selector,
            html: el.html,
            target: el.target,
            pageUrl: el.pageUrl,
            failureSummary: el.failureSummary,
          }))
        : issue.element
          ? [
              {
                selector: issue.element.selector,
                html: issue.element.html,
                target: issue.element.target,
              },
            ]
          : undefined,
    affectedPages: issue.affectedPages,
    remediation: issue.remediation,
    helpUrl: issue.referenceUrl,
    pageUrl: issue.pageUrl,
    needsManualReview: issue.needsManualReview,
  });

  // Exclude issues that have an active risk acceptance (reviewed / accepted).
  // (Manual review is now a server-side filter — see fetchIssues — so totalCount
  // and pagination reflect it, instead of filtering the current page client-side.)
  const acceptedIds = new Set(acceptedRisks.map((r) => r.issueId));
  const reviewedLinkUrls = new Set(reviewedLinks);
  // A ticked link stays on the list, marked as checked, rather than vanishing: this is
  // where the reader works through manual-review items, and a card that disappears
  // cannot be un-ticked if they change their mind. An accepted risk still hides — that
  // is a decision about the site, undone in the risk register.
  const visibleIssues = issues.filter(
    (i) => !acceptedIds.has(i.ruleId || i.clauseNumber || '')
  );

  // Only the link checks answer a question about this scan; everything else needing a
  // human check is a judgement about the site and goes through risk acceptance.
  const isCheckableLink = (issue: UnifiedIssue) =>
    !!onToggleLinkReviewed &&
    !!issue.needsManualReview &&
    !!issue.ruleId &&
    (issue.category === 'BROKEN_LINK' || issue.category === 'TOXIC_LINK');

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <IssueFilters
        filters={filters}
        onFiltersChange={setFilters}
        sort={sort}
        onSortChange={setSort}
        totalCount={totalCount}
        isLoading={isLoading}
      />

      {/* Error state */}
      {error && (
        <div className="card p-6 text-center">
          <AlertCircle size={48} className="mx-auto text-severity-serious mb-4" />
          <p className="text-severity-serious mb-4">{error}</p>
          <button onClick={() => fetchIssues(pageTokens[currentPage])} className="btn-secondary">
            Try Again
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && !error && <IssueCardSkeleton count={5} />}

      {/* Issues list */}
      {!isLoading && !error && (
        <>
          {visibleIssues.length === 0 ? (
            <div className="card p-8 text-center">
              <p className="text-gray-medium">
                {filters.severity.length > 0 ||
                filters.category.length > 0 ||
                filters.search ||
                filters.pageUrl ||
                filters.manualReview
                  ? 'No issues match your filters. Try adjusting your criteria.'
                  : 'No issues found for this scan.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {visibleIssues.map((issue) => (
                <IssueCard
                  key={issue.id}
                  issue={convertToIssueCardData(issue)}
                  showPageUrl
                  checked={!!issue.ruleId && reviewedLinkUrls.has(issue.ruleId)}
                  onToggleChecked={
                    isCheckableLink(issue)
                      ? (checked) => onToggleLinkReviewed!([issue.ruleId!], checked)
                      : undefined
                  }
                  onMarkReviewed={
                    // "Mark as reviewed" accepts the risk for the whole site and
                    // carries into the next scan. Link checks re-probe every scan,
                    // so a link review is only ever valid for this scan and gets its
                    // own control instead of this one.
                    onMarkReviewed &&
                    issue.category !== 'BROKEN_LINK' &&
                    issue.category !== 'TOXIC_LINK'
                      ? () =>
                          onMarkReviewed({
                            issueId: issue.ruleId || issue.clauseNumber || '',
                            issueDescription: issue.message,
                            severity: issue.severity,
                            category: issue.category,
                            pageUrls: issue.affectedPages,
                          })
                      : undefined
                  }
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-light">
              <p className="text-body-sm text-gray-medium">
                Showing {startItem} - {endItem} of {totalCount} issues
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-body-sm text-gray-medium px-3">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={!nextToken}
                  className="btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
